import { Button, Image, VerifiedBadge } from "@humanex/ui/components";
import { cn, useI18n } from "@humanex/ui/libs";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { PenLine, Plus, Star } from "lucide-react";

import { OrgAbout } from "./-components/org-about";

export const Route = createFileRoute("/_authenticated/org/$orgId/")({
  component: RouteComponent
});

function RouteComponent() {
  const { formatDate, t } = useI18n();
  const navigate = useNavigate();
  const { org } = useRouteContext({ from: "/_authenticated/org/$orgId/" });

  const handleCreateProgram = () => {
    if (!org?.id) return;
    navigate({ to: "/org/$orgId/program/create", params: { orgId: org.id } });
  };

  const onEditBtnClick = () => {
    if (!org?.id) return;
    navigate({ to: "/org/$orgId/edit", params: { orgId: org.id } });
  };

  if (!org) {
    return <div>{t("org.notFound")}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Banner */}
      <div
        className={cn(
          "relative bg-background-secondary transform duration-500 ease-in-out",
          "h-[200px] sm:h-[260px] lg:h-[280px]"
        )}
      >
        <Image src={org.cover} alt={org.name} className="w-full h-full object-cover rounded-2xl shadow-sm" />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto w-full h-full pb-16">
        {/* Company Header */}
        <div className={cn("relative mt-8 mb-8 flex flex-col sm:flex-row items-start gap-6", "px-0 sm:px-4")}>
          {/* Logo */}
          <div
            className={cn(
              "rounded-xl overflow-hidden bg-card border-[3px] border-border-light shadow-lg transform duration-500 ease-in-out",
              "w-28 h-28 sm:w-36 sm:h-36"
            )}
          >
            <Image src={org.avatar} alt={org.name} className="w-full h-full object-cover" />
          </div>

          {/* Info */}
          <div className="flex-1 space-y-3 flex flex-col sm:flex-row justify-between">
            <div className={cn("transform duration-500 ease-in-out", "space-y-3")}>
              <div className={cn("transform duration-500 ease-in-out", "space-y-1")}>
                <p className="item-center">
                  <span
                    className={cn(
                      "font-normal text-text-primary transform duration-500 ease-in-out",
                      "text-responsive-5xl"
                    )}
                  >
                    {org.name}
                  </span>
                  {org.verified && <VerifiedBadge className="inline-block ml-2" size="sm" />}
                </p>
                <p className="text-responsive-base text-text-primary font-normal">{org.tagline}</p>
              </div>

              <div className="flex items-center gap-1">
                <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                <span className="font-medium">{org.stats.rating}</span>
                <span className="text-muted-foreground">
                  ({org.stats.totalReviews} {t("org.reviews.count")})
                </span>
              </div>
              <p className="text-responsive-sm text-text-secondary font-medium">
                {t("org.created")}{" "}
                {formatDate(org.createdAt, {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
              </p>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={onEditBtnClick} variant="outline">
                <PenLine className="h-4 w-4 mr-2" />
                {t("common.edit")}
              </Button>
              <Button onClick={handleCreateProgram} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                {t("org.createProgram")}
              </Button>
            </div>
          </div>
        </div>

        <OrgAbout org={org} />
      </div>
    </div>
  );
}
